import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import API from "../../../utils/axios";

import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { getFormattedPhoneAndCountry } from "../../../utils/helpers";
import { useHistory } from "react-router-dom";
import { setDrivers } from "../../../store/actions/project";
import { setSolution } from "../../../store/actions/solution";
import { emailRegex } from "../../../utils/regExps";
import StyledDrawer from "../StyledDrawer";
import { useSnackbar } from "notistack";
import UnsavedChangesDialog from "../../dialogs/UnsavedChangesDialog";
import { IconButton } from "@material-ui/core";
import BackIcon from "@material-ui/icons/ArrowBack";

import OptimizedTooltip from "../../common/OptimizedTooltip";
import Form from "../../common/Form";

import { PhoneNumberUtil } from "google-libphonenumber";
import EditDialogActions from "../../common/EditDialogActions";
import mixpanelWrapper from "../../../utils/mixpanel";

const phoneUtil = PhoneNumberUtil.getInstance();

const useStyles = makeStyles(theme => ({
  header: {
    height: 80,
    borderBottom: "1px solid #eee",
    boxSizing: "border-box"
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500
  },
  subtitle: {
    lineHeight: "1.1875rem",
    fontWeight: 500
  }
}));

function DriversEditDialog({
  open,
  currentDriver,
  countryCode,
  solution,
  setDrivers,
  setSolution,
  setCurrentAction,
  editingOutsideId,
  saveDisabled,
  setSaveDisabled,
  drivers,
  tags,
  sortedTags,
  setBackFromEditAction
}) {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [inputValues, setInputValues] = useState({
    firstName: currentDriver ? currentDriver.first_name : "",
    lastName: currentDriver ? currentDriver.last_name : "",
    email: currentDriver ? currentDriver.email : "",
    phone: currentDriver ? currentDriver.mobile_number : "",
    phoneCountry: ""
  });

  const [selectedTags, setSelectedTags] = useState(
    currentDriver
      ? currentDriver.tags
          .filter(currentTag => tags.find(tag => tag.id === currentTag.id))
          .map(tag => tag.id)
      : []
  );

  useEffect(() => {
    setInputValues({
      firstName: currentDriver ? currentDriver.first_name : "",
      lastName: currentDriver ? currentDriver.last_name : "",
      email: currentDriver ? currentDriver.email : "",
      phone: currentDriver ? currentDriver.mobile_number : "",
      phoneCountry: ""
    });
    setSelectedTags(
      currentDriver
        ? currentDriver.tags
            .filter(currentTag => tags.find(tag => tag.id === currentTag.id))
            .map(tag => tag.id)
        : []
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentDriver]);

  const currentProject = sessionStorage.currentProject;
  const [changesDialog, setChangesDialog] = useState({
    visible: false,
    action: () =>
      editingOutsideId
        ? history.push(currentProject ? `/${currentProject}` : "/")
        : setCurrentAction("overview")
  });

  const initialErrors = {
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  };

  const [errors, setErrors] = useState({ ...initialErrors });

  const handleChange = e => {
    if (inputValues[e.target.name].trim() !== e.target.value.trim()) {
      setSaveDisabled(false);
    }
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (val, country) => {
    console.log(country);
    setInputValues({
      ...inputValues,
      phone: val,
      phoneCountry: country && country.dialCode
    });
    setSaveDisabled(false);
  };

  const checkForErrors = () => {
    const phone = inputValues.phone;
    const { numberVal } = getFormattedPhoneAndCountry(phone || "");

    const number =
      numberVal.length > 4 &&
      (inputValues.phone.includes("+")
        ? phoneUtil.parseAndKeepRawInput(inputValues.phone)
        : phoneUtil.parseAndKeepRawInput("+" + inputValues.phone));

    const newErrors = {
      email:
        inputValues.email.trim().length === 0
          ? "Please fill in this field"
          : !emailRegex.test(inputValues.email.trim())
          ? "Invalid email address"
          : "",
      firstName:
        inputValues.firstName.trim().length === 0
          ? "Please fill in this field"
          : "",
      lastName:
        inputValues.lastName.trim().length === 0
          ? "Please fill in this field"
          : "",
      phone:
        numberVal.length && inputValues.phoneCountry !== numberVal
          ? !number || !phoneUtil.isValidNumber(number)
            ? "Please enter a valid phone number"
            : ""
          : ""
    };

    setErrors(newErrors);

    return Object.values(newErrors).some(val => val);
  };

  const { enqueueSnackbar } = useSnackbar();

  // Save / Edit driver
  const handleSave = () => {
    if (checkForErrors()) return;
    setLoading(true);

    const { numberVal } = getFormattedPhoneAndCountry(inputValues.phone || "");
    const edit = currentDriver && currentDriver.id;

    let data = [
      {
        first_name: inputValues.firstName,
        last_name: inputValues.lastName,
        email: inputValues.email,
        mobile_number:
          numberVal !== inputValues.phoneCountry && inputValues.phone.length > 1
            ? inputValues.phone
            : null,
        tags: selectedTags
      }
    ];

    // Define api request type & url
    const method = edit ? "patch" : "post";
    const url = `/v1/drivers${edit ? `/${currentDriver.id}` : "/batch"}`;

    // If edit set single object without array
    if (edit) data = data[0];

    API[method](url, data)
      .then(res => {
        if (method === "post") {
          mixpanelWrapper.track("Driver Add");
        }
        setDrivers(res.data);
        if (edit && solution) {
          const newSolution = { ...solution };
          newSolution.routes.forEach(route => {
            if (route.driver && route.driver.id === currentDriver.id) {
              route.driver = {
                ...currentDriver,
                ...data
              };
            }
          });
          setSolution(newSolution);
        }
        enqueueSnackbar(
          `Driver "${inputValues.firstName} ${inputValues.lastName}" ${
            edit ? "edited" : "added"
          }.`
        );
        if (editingOutsideId) {
          history.push(currentProject ? `/${currentProject}` : "/");
        } else {
          setCurrentAction("overview");
        }
        setSaveDisabled(true);
        setLoading(false);
      })
      .catch(err => {
        if (err && err.status === 400 && err.data && err.data.errors[0]) {
          let field = err.data.errors[0].field;
          if (field === "driverMail") {
            field = "email";
          } else if (field === "firstname/lastname/email/phone") {
            enqueueSnackbar(err.data.errors[0].defaultMessage);
            setSaveDisabled(true);
          }
          setErrors({
            ...initialErrors,
            [field]: err.data.errors[0].defaultMessage
          });
        } else {
          enqueueSnackbar("Something went wrong, couldn't update driver");
        }
        setLoading(false);
        console.log(err);
      });
  };

  // Warn about unsaved changes before closing
  const warnClose = saveDisabled
    ? () => {
        if (editingOutsideId) {
          history.push(currentProject ? `/${currentProject}` : "/");
        } else {
          setLoading(false);
          setCurrentAction("overview");
        }
      }
    : () =>
        setChangesDialog({
          visible: true,
          action: () => {
            if (editingOutsideId) {
              history.push(currentProject ? `/${currentProject}` : "/");
            } else {
              setLoading(false);

              setCurrentAction("overview");
            }
          }
        });

  const handleTagSelect = tag => {
    if (selectedTags.includes(tag.id)) {
      setSelectedTags(
        selectedTags.filter(selectedTag => selectedTag !== tag.id)
      );
    } else {
      setSelectedTags([...selectedTags, tag.id]);
    }
    setSaveDisabled(false);
  };

  const autoFocusInput = useRef();

  useEffect(() => {
    if (open && autoFocusInput.current) autoFocusInput.current.focus();
  }, [open]);

  return (
    <>
      <StyledDrawer handleClose={warnClose} open={open} loading={loading}>
        {/* Header */}
        <Box
          px={3}
          className={classes.header}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
        >
          <OptimizedTooltip placement="bottom" title="Close">
            <Box ml={-1} mr={2}>
              <IconButton
                onClick={() => {
                  warnClose();
                  // setBackFromEditAction(true);
                }}
                data-cy="driver-closeEdit"
              >
                <BackIcon />
              </IconButton>
            </Box>
          </OptimizedTooltip>
          <Box alignSelf="center">
            <Typography variant="h5" className={classes.title}>
              {currentDriver && currentDriver.id ? "Edit driver" : "Add driver"}
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <Box pt={4} pb={1} pl={3} pr={5} mb={1.5}>
          {/* Name */}
          <Box display="flex" alignItems="center" mb={2}>
            <Typography className={classes.subtitle}>Driver details</Typography>
          </Box>

          {/* Form */}
          <Form
            data={[
              [
                {
                  renderInputField: true,
                  label: "First name*",
                  name: "firstName",
                  type: "text",
                  inputRef: autoFocusInput,
                  autoFocus: true,
                  required: "*Required",
                  dataCy: "driver-firstName",
                },
                {
                  renderInputField: true,
                  label: "Last name*",
                  name: "lastName",
                  type: "text",
                  dataCy: "driver-lastName",
                  required: "*Required"
                }
              ],
              [
                {
                  renderInputField: true,
                  label: "Email*",
                  name: "email",
                  type: "text",
                  dataCy: "driver-email",
                  required: "*Required"
                }
              ],
              [
                {
                  label: "Mobile phone",
                  name: "phone",
                  componentType: "phone",
                  dataCy: "driver-phone"
                }
              ],
              [
                {
                  labelId: "tag-driver",
                  selectId: "tag-driver-select",
                  label: "Tags",
                  name: "tag",
                  type: "driver",
                  choose: false,
                  options: sortedTags,
                  // shrink: true,
                  selected: selectedTags,
                  // dataCy: "tag-select-type",
                  onChange: handleTagSelect,
                  componentType: "tagSelect"
                }
              ]
            ]}
            inputValues={inputValues}
            errorValues={errors}
            countryCode={countryCode}
            handleChange={handleChange}
            handlePhoneChange={handlePhoneChange}
          />
        </Box>

        {/* Save */}
        <EditDialogActions
          handleClose={warnClose}
          handleClick={() => {
            handleSave();
          }}
          disabled={saveDisabled}
        />
      </StyledDrawer>
      <UnsavedChangesDialog
        open={changesDialog.visible}
        handleClose={() => {
          setChangesDialog({ visible: false, action: () => {} });
        }}
        action={() => {
          changesDialog.action();
          setSaveDisabled(true);
        }}
      />
    </>
  );
}

const mapStateToProps = state => {
  let countryCode;

  // Get country code from organisation site for non-freemium users
  const organisationSite = state.auth.userData.organisationSite;
  if (organisationSite && organisationSite.organisation) {
    countryCode = organisationSite.organisation.country;
  }

  return {
    countryCode: countryCode || state.auth.userData.countryName,
    solution: state.project.solution,
    drivers: state.project.drivers || [],
    sortedTags: state.account.sortedTags,
    tags: state.account.tags
  };
};

export default connect(mapStateToProps, {
  setDrivers,
  setSolution
})(DriversEditDialog);
